import { Check } from "lucide-react";
import { SIZES, PUMPKIN_COLORS, BYO_TIERS, BYO, colorLabel } from "@/data/collections";
import { Counter } from "@/components/AddOnItem";

const money = (n) => `$${Number(n).toLocaleString()}`;

const line = "var(--pp-border)";
const muted = "var(--pp-text-muted)";
const isLight = (hex) => parseInt(hex.slice(1, 3), 16) + parseInt(hex.slice(3, 5), 16) + parseInt(hex.slice(5, 7), 16) > 600;

const hexOf = (id) => PUMPKIN_COLORS.find((c) => c.id === id)?.hex || "#ccc";
const PalettePreview = ({ palette: p }) => {
  if (!p.colors.length) return <p className="mb-4 text-xs" style={{ color: muted }} data-testid="palette-preview-empty">Pick a few colors above and your blend will appear here.</p>;
  const custom = p.balance === "custom" && p.primary;
  const weights = p.colors.map((c) => (custom ? (c === p.primary ? 4 : c === p.accent ? 1 : 2) : 1));
  const total = weights.reduce((a, b) => a + b, 0);
  return (
    <div className="mb-4" data-testid="palette-preview">
      <div className="pp-caption mb-2">Your blend</div>
      <div className="flex h-8 w-full overflow-hidden rounded-full" style={{ boxShadow: `0 0 0 1px ${line}` }}>
        {p.colors.map((c, i) => (
          <div key={c} data-testid={`palette-preview-${c}`} title={colorLabel(c)} className="h-full transition-[flex-grow] duration-500 ease-out" style={{ flexGrow: weights[i], flexBasis: 0, backgroundColor: hexOf(c), borderLeft: i ? "1px solid rgba(255,255,255,0.35)" : "none" }} />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]" style={{ color: muted }}>
        {p.colors.map((c, i) => (
          <span key={c} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: hexOf(c), boxShadow: `0 0 0 1px ${line}` }} />{colorLabel(c)} · {Math.round((weights[i] / total) * 100)}%</span>
        ))}
      </div>
    </div>
  );
};


export const BuildYourOwn = ({ booking, update }) => {
  const p = booking.palette;
  const setPalette = (patch) => update({ palette: { ...p, ...patch } });
  const toggle = (id) => {
    const colors = p.colors.includes(id) ? p.colors.filter((c) => c !== id) : [...p.colors, id];
    setPalette({ colors, primary: colors.includes(p.primary) ? p.primary : "", accent: colors.includes(p.accent) ? p.accent : "" });
  };

  return (
    <div className="space-y-10" data-testid="byo-panel">
      <div data-testid="byo-tier-section">
        <div className="pp-caption mb-2">Step 1</div>
        <h3 className="font-medium mb-4" style={{ color: "var(--pp-burgundy)" }}>Choose the scale of your porch</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SIZES.map((s) => {
            const active = booking.size === s.id;
            return (
              <button key={s.id} type="button" data-testid={`size-button-${s.id}`} onClick={() => update({ size: s.id })} className="text-center py-5 px-2 transition-colors"
                style={{ backgroundColor: active ? "var(--pp-terracotta)" : "var(--pp-cream-card)", color: active ? "var(--pp-parchment)" : "var(--pp-burgundy)", border: `1px solid ${active ? "var(--pp-terracotta)" : line}` }}>
                <div className="font-serif-display text-2xl">{s.label}</div>
                <div className="text-sm mt-1" style={{ color: active ? "#F6E6D4" : muted }}>from {money(BYO_TIERS[s.id].from)}</div>
                <div className="text-xs mt-0.5" style={{ color: active ? "#F6E6D4" : muted }}>{BYO_TIERS[s.id].pumpkins}</div>
              </button>
            );
          })}
        </div>
        {booking.size && <p className="mt-3 text-xs italic" style={{ color: muted }} data-testid="byo-tier-note">{BYO_TIERS[booking.size].note}. Includes a flat {money(BYO.designFee)} custom palette design consultation; {money(BYO.minimum)} order minimum.</p>}
        {booking.size === "estate" && (
          <div className="mt-4 flex items-center justify-between gap-4 border p-4" style={{ borderColor: line, backgroundColor: "var(--pp-cream-card)" }} data-testid="byo-extra-pumpkins">
            <div>
              <div className="text-sm font-medium" style={{ color: "var(--pp-burgundy)" }}>Additional pumpkins beyond {BYO.extraPumpkinFrom}</div>
              <div className="text-xs" style={{ color: muted }}>{money(BYO.extraPumpkinPrice)} each</div>
            </div>
            <Counter value={booking.extraPumpkins || 0} onChange={(v) => update({ extraPumpkins: Math.max(0, Math.floor(Number.isFinite(v) ? v : 0)) })} testId="byo-extra-pumpkins" />
          </div>
        )}
      </div>

      <div data-testid="byo-colors-section">
        <div className="pp-caption mb-2">Step 2</div>
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="font-medium" style={{ color: "var(--pp-burgundy)" }}>Choose your pumpkin colors</h3>
          <span className="text-xs" style={{ color: muted }} data-testid="byo-color-count">{p.colors.length} chosen</span>
        </div>
        <div className="flex flex-wrap gap-4" role="group" aria-label="Pumpkin colors">
          {PUMPKIN_COLORS.map((c) => {
            const on = p.colors.includes(c.id);
            return (
              <button key={c.id} type="button" data-testid={`color-dot-${c.id}`} aria-pressed={on} onClick={() => toggle(c.id)} title={c.label} className="flex flex-col items-center gap-1.5 w-16 group">
                <span className="relative h-11 w-11 rounded-full flex items-center justify-center transition-transform group-hover:scale-105"
                  style={{ backgroundColor: c.hex, boxShadow: on ? "0 0 0 2px var(--pp-cream), 0 0 0 4px var(--pp-terracotta)" : `0 0 0 1px ${line}` }}>
                  {on && <Check className="h-5 w-5" strokeWidth={2.5} style={{ color: isLight(c.hex) ? "var(--pp-burgundy)" : "var(--pp-parchment)" }} />}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-center leading-tight" style={{ color: on ? "var(--pp-burgundy)" : muted }}>{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div data-testid="byo-balance-section">
        <div className="pp-caption mb-2">Step 3</div>
        <h3 className="font-medium mb-4" style={{ color: "var(--pp-burgundy)" }}>How should we balance them?</h3>
        <PalettePreview palette={p} />
        <div className="grid grid-cols-2 gap-3">
          {[["equal", "Equal Blend"], ["custom", "Custom Balance"]].map(([id, label]) => {
            const active = p.balance === id;
            return (
              <button key={id} type="button" data-testid={`balance-${id}`} aria-pressed={active} onClick={() => setPalette({ balance: id })} className="py-4 px-3 text-sm transition-colors"
                style={{ backgroundColor: active ? "var(--pp-burgundy)" : "var(--pp-cream-card)", color: active ? "var(--pp-parchment)" : "var(--pp-burgundy)", border: `1px solid ${active ? "var(--pp-burgundy)" : line}` }}>
                {label}
              </button>
            );
          })}
        </div>
        {p.balance === "equal" ? (
          <p className="mt-3 text-sm" style={{ color: muted }} data-testid="balance-equal-help">We'll mix all your chosen colors evenly throughout the display.</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="balance-custom-fields">
            <Select label="Primary color (most of display)" testId="primary-color-select" value={p.primary} onChange={(v) => setPalette({ primary: v })} options={p.colors} />
            <Select label="Accent color (least of display)" testId="accent-color-select" value={p.accent} onChange={(v) => setPalette({ accent: v })} options={p.colors} />
            {p.colors.length === 0 && <p className="sm:col-span-2 text-xs" style={{ color: muted }}>Choose a few colors above first.</p>}
          </div>
        )}
      </div>

      <div data-testid="byo-notes-section">
        <div className="pp-caption mb-2">Step 4</div>
        <label className="block">
          <span className="font-medium block mb-3" style={{ color: "var(--pp-burgundy)" }}>Any specific notes for your porch design?</span>
          <textarea data-testid="customer-notes-input" rows={4} maxLength={1000} value={booking.customerNotes} onChange={(e) => update({ customerNotes: e.target.value })}
            placeholder="e.g., Heavy on unique heirloom shapes, keep main walkway completely open, matching door frame accents..."
            className="w-full border p-3 text-sm outline-none focus:border-[var(--pp-terracotta)]" style={{ borderColor: line, backgroundColor: "var(--pp-cream-card)" }} />
        </label>
      </div>
    </div>
  );
};

const Select = ({ label, testId, value, onChange, options }) => (
  <label className="block">
    <span className="pp-caption block mb-1">{label}</span>
    <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border px-3 py-2.5 text-sm outline-none focus:border-[var(--pp-terracotta)]"
      style={{ borderColor: line, backgroundColor: "var(--pp-cream-card)", color: "var(--pp-text)" }}>
      <option value="">Select a color</option>
      {options.map((id) => <option key={id} value={id}>{colorLabel(id)}</option>)}
    </select>
  </label>
);
